import React, { useRef, useState } from 'react';
import { Delete24Regular, ArrowUpload24Regular, Camera24Regular } from '@fluentui/react-icons';
import { Button } from '@fluentui/react-components';
import { showToast } from "#root/components/GlobalToaster";
import WebcamCapture from '#root/components/WebcamCapture';
import './styles.scss';

export interface UploaderFile extends File {
  id?: string;
}

interface ImageUploaderProps {
  files: UploaderFile[];
  onChange: (files: UploaderFile[]) => void;
  maxFiles?: number;
  maxFileSize?: number;
  placeholder?: string;
  onFileDelete?: (file: UploaderFile) => void;
  readOnly?: boolean;
  capture?: 'environment' | 'user';
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  files,
  onChange,
  maxFiles = 1,
  maxFileSize = 20 * 1024 * 1024, // 20MB
  placeholder = 'Add a Picture',
  onFileDelete,
  readOnly = false,
  capture
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [webcamOpen, setWebcamOpen] = useState(false);

  const handleCaptureClick = () => {
    if (capture) {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        showToast('Camera not available to take a photo.', 'error');
        return;
      }

      setWebcamOpen(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleWebcamCapture = (file: File) => {
    setWebcamOpen(false);

    if (maxFileSize && file.size > maxFileSize) {
      const maxSizeMB = maxFileSize / (1024 * 1024);
      showToast(`File size exceeds the ${maxSizeMB}MB limit`, 'error');
      return;
    }

    const updatedFiles = [...files, file as UploaderFile].slice(0, maxFiles);
    onChange(updatedFiles);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || []) as UploaderFile[];

    if (newFiles.length === 0 && capture) {
      showToast('Camera not available to take a photo.', 'error');
      return;
    }

    if (maxFileSize) {
      const oversizedFiles = newFiles.filter(file => file.size > maxFileSize);
      if (oversizedFiles.length > 0) {
        const maxSizeMB = maxFileSize / (1024 * 1024);
        showToast(`File size exceeds the ${maxSizeMB}MB limit`, 'error');
        if (event.target.value) event.target.value = '';
        return;
      }
    }

    const allowedTypes = ['image/jpeg', 'image/png'];
    const validFiles = newFiles.filter(file => allowedTypes.includes(file.type));

    if (validFiles.length !== newFiles.length) {
      showToast('Some files are not supported. Upload only JPEG and PNG images.', 'error');
    }

    const updatedFiles = [...files, ...validFiles].slice(0, maxFiles);
    onChange(updatedFiles);
    if (event.target.value) event.target.value = '';
  };

  const handleDelete = (index: number) => {
    const fileToDelete = files[index];
    if (fileToDelete.id && onFileDelete) {
      onFileDelete(fileToDelete);
    }
    const updatedFiles = files.filter((_, i) => i !== index);
    onChange(updatedFiles);
  };

  const showUploader = files.length < maxFiles;

  return (
    <div className="image-uploader">
      {files.map((file, index) => (
        <div key={index} className="image-preview">
          <img src={URL.createObjectURL(file)} alt={`Upload ${index + 1}`} />
          {!readOnly && (
            <Button
              icon={<Delete24Regular />}
              appearance="secondary"
              type="button"
              size="small"
              className="delete-button"
              onClick={() => handleDelete(index)}
              title="Remove image"
            />
          )}
        </div>
      ))}
      
      {showUploader && !readOnly && (
        <div
          className="upload-box"
          onClick={handleCaptureClick}
        >
          {capture ? (
            <Camera24Regular className="upload-icon" />
          ) : (
            <ArrowUpload24Regular className="upload-icon" />
          )}
          <div className="upload-text">{capture ? 'Take a Photo' : placeholder}</div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept={capture ? 'image/*' : 'image/png,image/jpeg'}
            style={{ display: 'none' }}
            {...(capture ? { capture } : {})}
          />
        </div>
      )}

      {capture && (
        <WebcamCapture
          open={webcamOpen}
          onCapture={handleWebcamCapture}
          onClose={() => setWebcamOpen(false)}
        />
      )}
    </div>
  );
};

export default ImageUploader;
